import React, { useEffect, useState } from "react";
import {
	Table,
	Button,
	Row,
	Col,
	Tag,
	Card,
	Form,
	Select,
	Input,
	notification,
	Modal,
	DatePicker,
} from "antd";
import { SolutionOutlined } from "@ant-design/icons";
import DefaultLayout from "../../components/layout/DefaultLayout";
import Header from "../../components/header";
import equipmentRequestService from "../../services/equipmentRequest";
import commonCrud from "../../services/commonCrud";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { Excel } from "antd-table-saveas-excel";
import { useMediaQuery } from "react-responsive";
import TableActionBtnsComponent from "../../components/tableActionsBtns";
import dayjs from "dayjs";

const { confirm } = Modal;

const searchOptions = [
	{ key: "sequence_no", value: "Sequence No" },
	{ key: "request_title", value: "Request Title" },
	{ key: "request_specs", value: "Request Specs" },
	{ key: "change_type", value: "Change Type" },
	{ key: "change_priority", value: "Change Priority" },
	{ key: "man_hours", value: "Man Hours" },
	{ key: "process_efficiency", value: "Process Efficiency" },
	{ key: "controls_improved", value: "Controls Improved" },
	{ key: "cost_saved", value: "Cost Saved" },
	{ key: "legal_reasons", value: "Legal Reasons" },
	{ key: "change_significance", value: "Change Significance" },
	{ key: "other_benefits", value: "Other Benefits" },
	{ key: "workflow_id", value: "Workflow" },
	{ key: "software_category", value: "Software Category" },
	{ key: "business_expert_id", value: "Business Expert" },
];

const GetEquipmentRequest = () => {
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [expandedRowKeys, setExpandedRowKeys] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalDataCount, setTotalDataCount] = useState(0);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [activeSearchParams, setActiveSearchParams] = useState(null);
	const [costCenters, setCostCenters] = useState([]);
	const [costCentersLoaded, setCostCentersLoaded] = useState(false);
	const [costCentersLoading, setCostCentersLoading] = useState(false);
	const { currentUser } = useSelector((state) => state.user);
	const [searchForm] = Form.useForm();
	const [formVisible, setFormVisible] = useState(false);
	const [formId] = useState(4);
	const [searchValues, setSearchValues] = useState({});
	const { RangePicker } = DatePicker;

	const allowedPermissions = currentUser?.["roles.permission"]?.map(
		(permission) => permission.name
	);

	const hasPermission = (permission) =>
		allowedPermissions?.includes(permission);



	// FOR SAVE STATE
	const crfLocalKey = currentUser?.user_id ? `crfLocal_${currentUser.user_id}` : null;
	const CRF_TTL_MS = 24 * 60 * 60 * 1000;

	const saveTableState = (state) => {
		if (!crfLocalKey) return;
		localStorage.setItem(crfLocalKey, JSON.stringify({ ...state, savedAt: Date.now() }));
	};

useEffect(() => {
		if (!crfLocalKey) return;

		const saved = localStorage.getItem(crfLocalKey);

		if (saved) {
			const parsed = JSON.parse(saved);
			if (parsed.savedAt && Date.now() - parsed.savedAt > CRF_TTL_MS) {
				localStorage.removeItem(crfLocalKey);
				getEquipmentsRequest();
				return;
			}

			setSearchValues(parsed.searchValues || {});
			setCurrentPage(parsed.page || 1);
			setItemsPerPage(parsed.perPage || 15);

			if (
				parsed.searchValues?.created_at_range &&
				Array.isArray(parsed.searchValues.created_at_range)
			) {
				parsed.searchValues.created_at_range =
					parsed.searchValues.created_at_range.map((date) =>
						dayjs(date)
					);
			}
			searchForm.setFieldsValue(parsed.searchValues || {});

        // ✅ Use onSearch to apply filters
        onSearch(
            parsed.searchValues || {},
            parsed.page || 1,
            parsed.perPage || 15,
            parsed.sortBy || "",
            parsed.sortOrder || ""
        );
    } else {
        getEquipmentsRequest();
    }
}, []);		

	const onSearch = async (values, page = 1, perPage = itemsPerPage, sortBy = "", sortOrder = "") => {

		setSearchValues(values);

		const formattedSearchValues = {
			...searchValues,
			created_at_range: searchValues.created_at_range
				? searchValues.created_at_range.map((d) =>
						d.format("YYYY-MM-DD")
				)
				: undefined,
		};
		saveTableState({
			searchValues: formattedSearchValues,
			page: 1,
			perPage: itemsPerPage,
			sortBy: "",
			sortOrder: "",
		});

		setLoading(true);
		setActiveSearchParams(values);
		try {
			const response = await equipmentRequestService.searchCrf(values, page, perPage, sortBy, sortOrder);
			setData(response.data);
			setCurrentPage(response.meta?.current_page ?? response.current_page ?? page);
			setTotalDataCount(response.meta?.total ?? response.total ?? 0);
			setItemsPerPage(response.meta?.per_page ?? response.per_page ?? perPage);
			setLoading(false);
		} catch (error) {
			console.error("Error while searching:", error);
			setLoading(false);
		}
	};

	const handleClearFilters = () => {
		searchForm.resetFields();
		setSearchValues({});
		setActiveSearchParams({});
		setCurrentPage(1);

		if (crfLocalKey) {
			localStorage.removeItem(crfLocalKey);
		}

		getEquipmentsRequest(1, itemsPerPage, "", "");
	};

	const handleCostCenterDropdownOpen = async (open) => {
		if (!open || costCentersLoaded) return;
		setCostCentersLoading(true);
		try {
			const data = await commonCrud.cost_centers();
			setCostCenters(data);
			setCostCentersLoaded(true);
		} catch (err) {
			console.error("Failed to load cost centers:", err);
		} finally {
			setCostCentersLoading(false);
		}
	};

	const toggleFormVisibility = () => {
		setFormVisible(!formVisible);
	};

	const handleDelete = (record) => {
		confirm({
			title: "Are you sure you want to delete this Capital Request Form?",
			content: "This action cannot be undone.",
			okText: "Yes",
			okType: "danger",
			cancelText: "No",
			onOk: async () => {
				try {
					const response = await equipmentRequestService.deleteCrf(record.id);
					if (response.success) {
						notification.success({
							message: "Capital Request Form Deleted",
							description: response.message,
						});
						await getEquipmentsRequest(currentPage, itemsPerPage);
					}
				} catch (error) {
					notification.error({
						message: "Error",
						description: error.response.data.message,
					});
				}
			},
			onCancel() {
				console.log("Delete action cancelled");
			},
		});
	};

	const columns = [
		{
			title: "Sequence No",
			dataIndex: "sequence_no",
			key: "sequence_no",
			fixed: useMediaQuery({ minWidth: 768 }) ? "left" : null,
			sorter: true,
		},
		{
			title: "Request Title",
			dataIndex: "request_title",
			key: "request_title",
			sorter: true,
			render: (text) => (text.length > 40 ? `${text.slice(0, 40)}...` : text),
		},
		{
			title: "Initiated By",
			dataIndex: ["created_by", "name"],
			key: "created_by",
			sorter: true,
		},
		{
			title: "Location",
			dataIndex: ["location", "name"],
			key: "location",
			sorter: true,
		},
		{
			title: "Cost Center",
			dataIndex: ["cost_center_id", "cost_center"],
			key: "cost_center",
			sorter: true,
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 100,
			render: (status) => (
				<Tag
					color={
						(status === "Pending" && "orange") ||
						(status === "Approved" && "green") ||
						(status === "Return" && "red")
					}
				>
					{status}
				</Tag>
			),
			sorter: true,
		},
		{
			title: "Created At",
			dataIndex: "created_at",
			width: 120,
			key: "created_at",
			sorter: true,
		},
		// {
		//   title: 'Actions',
		//   dataIndex: 'Actions',
		//   key: 'Actions',
		//   width: 80,
		//   fixed: useMediaQuery({ minWidth: 768 }) ? 'right' : null,
		//   render: (text, record) => (
		//     <TableActionBtnsComponent record={record} handleDelete={handleDelete} currentUser={currentUser} formId={formId}
		//      />
		//   ),
		// },
		{
			title: "Actions",
			dataIndex: "Actions",
			key: "Actions",
			width: 120, // wider for spacing
			fixed: "right",
			render: (text, record) => (
				<TableActionBtnsComponent
					record={record}
					handleDelete={handleDelete}
					currentUser={currentUser}
					formId={formId}
				/>
			),
		},
	];

	const expandedRowRender = (record) => {
		const equipmentColumns = [
			{
				title: "Qty",
				dataIndex: "qty",
				key: "qty",
			},
			{
				title: "State",
				dataIndex: ["state", "name"],
				key: ["state", "name"],
			},
			{
				title: "Expense Type",
				dataIndex: ["expense_type", "name"],
				key: ["expense_type", "name"],
			},
			{
				title: "Expense Nature",
				dataIndex: ["expense_nature", "name"],
				key: ["expense_nature", "name"],
			},
			{
				title: "Business Justification",
				dataIndex: "business_justification",
				key: "business_justification",
			},
		];

		const softwareColumns = [
			{
				title: "Name",
				dataIndex: "name",
				key: "software_name",
			},
			{
				title: "Version",
				dataIndex: "version",
				key: "version",
			},
			{
				title: "Qty",
				dataIndex: "qty",
				key: "software_qty",
			},
			{
				title: "Expense Type",
				dataIndex: ["expense_type", "name"],
				key: ["expense_type", "name"],
			},
			{
				title: "Expense Nature",
				dataIndex: ["expense_nature", "name"],
				key: ["expense_nature", "name"],
			},
			{
				title: "Business Justification",
				dataIndex: "business_justification",
				key: "business_justification",
			},
		];

		const serviceColumns = [
			{
				title: "Name",
				dataIndex: "name",
				key: "service_name",
			},
			{
				title: "State",
				dataIndex: ["state", "name"],
				key: ["state", "name"],
			},
			{
				title: "Expense Type",
				dataIndex: ["expense_type", "name"],
				key: ["expense_type", "name"],
			},
			{
				title: "Expense Nature",
				dataIndex: ["expense_nature", "name"],
				key: ["expense_nature", "name"],
			},
			{
				title: "Business Justification",
				dataIndex: "business_justification",
				key: "business_justification",
			},
		];

		return (
			<div>
				{record.equipmentRequests && record.equipmentRequests.length > 0 && (
					<>
						{" "}
						<h4 className='mb-4 mt-4'>Equipment Requests:</h4>
						<Table
							columns={equipmentColumns}
							dataSource={record.equipmentRequests}
							pagination={false}
							style={{ borderLeft: "2px solid #1599fd" }}
						/>
					</>
				)}

				{record.softwareRequests && record.softwareRequests.length > 0 && (
					<>
						<h4 className='mb-4 mt-4'>Software Requests:</h4>
						<Table
							columns={softwareColumns}
							dataSource={record.softwareRequests}
							pagination={false}
							style={{ borderLeft: "2px solid #1599fd" }}
						/>
					</>
				)}
				{record.serviceRequests && record.serviceRequests.length > 0 && (
					<>
						<h4 className='mb-4 mt-4'>Service Requests:</h4>
						<Table
							columns={serviceColumns}
							dataSource={record.serviceRequests}
							pagination={false}
							style={{ borderLeft: "2px solid #1599fd" }}
						/>
					</>
				)}
			</div>
		);
	};

	// const handleClick = () => {
	//   const excelColumns = columns
	//     .filter(column => column.key !== 'Actions')
	//     .map(column => {
	//       const { render, ...columnWithoutRender } = column;
	//       return columnWithoutRender;
	//     });

	//   const excel = new Excel();
	//   excel
	//     .addSheet("data")
	//     .addColumns(excelColumns)
	//     .addDataSource(data, {
	//       str2Percent: true
	//     })
	//     .saveAs("Excel.xlsx");
	// };

	const handleExport = async () => {
		try {
			setLoading(true);

			const response = await equipmentRequestService.getEquipmentsRequest(
				1, // page
				1000, // ONLY for export
				"", // sortBy
				"" // sortOrder
			);

			const exportData = response.data;

			const excelColumns = columns
				.filter((col) => col.key !== "Actions")
				.map(({ render, ...col }) => col);

			const excel = new Excel();
			excel
				.addSheet("CRF")
				.addColumns(excelColumns)
				.addDataSource(exportData)
				.saveAs("Capital_Request_Form.xlsx");
		} catch (err) {
			console.error(err);
		} finally {
			setLoading(false);
		}
	};

	const getEquipmentsRequest = async (
		page = 1,
		itemsPerPage = 15,
		sortBy = "",
		sortOrder = ""
	) => {
		setLoading(true);
		console.log(page, itemsPerPage, sortBy, sortOrder);
		await equipmentRequestService
			.getEquipmentsRequest(page, itemsPerPage, sortBy, sortOrder)
			.then((response) => {
				//console.log(response)
				setTotalDataCount(response.meta.total);
				setCurrentPage(response.meta.current_page);
				setData(response.data);
				setLoading(false);
			});
	};


	return (
		<DefaultLayout>
			<Row gutter={[24, 24]}>
				<Col span={24}>
					<Header
						icon={<SolutionOutlined />}
						title='Capital Request Form'
						right={
							<>
								{/* 🔹 My Workflows Button */}
								{hasPermission("OwnWorkflow-view") && (
									<Link to={`/own-workflow?form_id=4`}>
										<Button className='mr-10'>My Workflows</Button>
									</Link>
								)}
								<Button onClick={handleExport} className='mr-10'>
									Export
								</Button>
								<Link to='/crf/add'>
									<Button className='btn-blue' type='primary'>
										Add Capital Request Form
									</Button>
								</Link>
							</>
						}
					/>
				</Col>
			</Row>

			<Card>
				<Button onClick={toggleFormVisibility}>
					{formVisible ? "Hide Search Form" : "Show Search Form"}
				</Button>

				{formVisible && (
					<Form form={searchForm} onFinish={onSearch} layout='vertical'>
						<Card className='pt-6' style={{ paddingBottom: "0 !important" }}>
							<Row gutter={[12, 12]}>
								{[
									{ key: "sequence_no", placeholder: "Sequence No" },
									{ key: "request_title", placeholder: "Request Title" },
									{ key: "created_by", placeholder: "Initiated By" },
									{ key: "location", placeholder: "Location" },
									{
										key: "equipmentRequests",
										placeholder: "Equipment Request",
									},
									{ key: "serviceRequests", placeholder: "Service Request" },
									{ key: "softwareRequests", placeholder: "Software Request" },
								].map(({ key, placeholder }) => (
									<Col key={key} lg={6} md={12} sm={24} xs={24}>
										<Form.Item name={key}>
											<Input size='large' placeholder={`${placeholder}`} />
										</Form.Item>
									</Col>
								))}
								{/* Cost Center lazy-load dropdown */}
								<Col lg={6} md={12} sm={24} xs={24}>
									<Form.Item name='costCenter'>
										<Select
											size='large'
											placeholder='Cost Center'
											allowClear
											showSearch
											loading={costCentersLoading}
											filterOption={(input, option) =>
												option?.label?.toLowerCase().includes(input.toLowerCase())
											}
											onOpenChange={handleCostCenterDropdownOpen}
											options={costCenters.map((cc) => ({
												value: cc.cost_center,
												label: cc.cost_center,
											}))}
										/>
									</Form.Item>
								</Col>
								<Col lg={6} md={12} sm={24} xs={24}>
									<Form.Item name='created_at_range'>
										<RangePicker
											size='large'
											style={{ width: "100%" }}
										/>
									</Form.Item>
								</Col>

								<Col lg={24} md={24} sm={24} xs={24}>
									<div style={{ display: "flex", gap: "10px" }}>
										<Button
											block
											className='btn-blue'
											htmlType='submit'
											type='primary'
										>
											Search
										</Button>
										<Button
											block
											onClick={handleClearFilters}
										>
											Clear
										</Button>
									</div>
								</Col>
							</Row>
						</Card>
					</Form>
				)}
			</Card>

			<Card>
				<Table
					columns={columns}
					dataSource={data.map((item) => ({ ...item, key: item.id }))}
					style={{ minHeight: "100vh" }}
					scroll={{ x: 1200 }}
					sticky={true}
					loading={loading}
					pagination={{
						showSizeChanger: true,
						pageSizeOptions: ["10", "15", "25", "50", "100"],
						current: currentPage,
						total: totalDataCount,
						pageSize: itemsPerPage,
					}}
					onChange={(pagination, filters, sorter) => {
						const { order, field } = sorter;
						const sortOrder =
							order === "ascend" ? "asc" : order === "descend" ? "desc" : "";
					if (activeSearchParams) {
							// ✅ save state
								saveTableState({
									searchValues,
									page: pagination.current,
									perPage: pagination.pageSize,
									sortBy: field,
									sortOrder,
								});
						onSearch(
							activeSearchParams,
							pagination.current,
							pagination.pageSize,
							field,
							sortOrder
						);
					} else {
						// ✅ save state
								saveTableState({
									searchValues,
									page: pagination.current,
									perPage: pagination.pageSize,
									sortBy: field,
									sortOrder,
								});

						getEquipmentsRequest(
							pagination.current,
							pagination.pageSize,
							field,
							sortOrder
						);
					}
					}}
					expandable={{
						expandedRowRender,
						expandedRowKeys,
						onExpand: (expanded, record) => {
							if (expanded) {
								setExpandedRowKeys((prevKeys) => [...prevKeys, record.id]);
							} else {
								setExpandedRowKeys((prevKeys) =>
									prevKeys.filter((key) => key !== record.id)
								);
							}
						},
					}}
				/>
			</Card>
		</DefaultLayout>
	);
};

export default GetEquipmentRequest;
